import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from '../lib/dates';
import { ApiError, asList } from '../lib/api';
import { transportApi, type Vehicle } from '../lib/transportApi';
import {
  expensesApi, EXPENSE_KINDS,
  type VehicleExpense, type VehicleCost, type ExpenseKind,
} from '../lib/expensesApi';
import { formatPaise, toPaise } from '../lib/money';

/* ============================================================================
   VEHICLE RUNNING COSTS

   Fuel, service, repairs, insurance, permits — per vehicle, with the bill and
   the meter photographed at the pump.

   ⚠ PHONE FIRST. This is used standing at a petrol bunk holding a paper slip,
   not at a desk. Every control is thumb-sized, the camera opens directly, and
   the form is short enough to finish before the driver pulls away.
   ========================================================================== */

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

export function VehicleExpensesPage() {
  const [tab, setTab] = useState<'entries' | 'report'>('entries');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<string>('');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());

  const [rows, setRows] = useState<VehicleExpense[]>([]);
  const [report, setReport] = useState<VehicleCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    transportApi.listVehicles().then((r) => setVehicles(asList(r))).catch(() => setVehicles([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (tab === 'entries') setRows(await expensesApi.list(vehicleId || null, from, to));
      else setReport(await expensesApi.costReport(from, to));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load.');
    } finally { setLoading(false); }
  }, [tab, vehicleId, from, to]);

  useEffect(() => { void load(); }, [load]);

  const total = rows.reduce((s, r) => s + r.amountPaise, 0);

  return (
    <div className="page">
      <div className="eyebrow" style={{ marginBottom: 6 }}>Transport</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 className="h-display" style={{ fontSize: 28, marginBottom: 4 }}>Vehicle costs</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 14 }}>Fuel, service and repairs, per vehicle.</p>
        </div>
        <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Add bill</button>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn on={tab === 'entries'} onClick={() => setTab('entries')}>Entries</TabBtn>
        <TabBtn on={tab === 'report'} onClick={() => setTab('report')}>Cost per vehicle</TabBtn>
      </div>

      {/* ---- filters ---- */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {tab === 'entries' && (
          <select className="input" value={vehicleId} style={{ maxWidth: 200 }}
            onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">All vehicles</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
        <input className="input" type="date" value={from} style={{ maxWidth: 165 }}
          onChange={(e) => setFrom(e.target.value)} />
        <input className="input" type="date" value={to} style={{ maxWidth: 165 }}
          onChange={(e) => setTo(e.target.value)} />
      </div>

      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}

      {!loading && tab === 'entries' && (
        <>
          {rows.length > 0 && (
            <div className="card" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-2)', fontSize: 13 }}>{rows.length} entries</span>
              <strong>{formatPaise(total)}</strong>
            </div>
          )}
          {rows.length === 0
            ? <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-3)' }}>
                No bills recorded for this period.
              </div>
            : <div style={{ display: 'grid', gap: 8 }}>
                {rows.map((r) => <ExpenseCard key={r.id} row={r} onChanged={load} />)}
              </div>}
        </>
      )}

      {!loading && tab === 'report' && <CostReport rows={report} />}

      {adding && (
        <AddExpenseDialog vehicles={vehicles} onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); void load(); }} />
      )}
    </div>
  );
}

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="btn" style={{
      border: 'none', borderBottom: on ? '2px solid var(--accent)' : '2px solid transparent',
      borderRadius: 0, background: 'transparent', fontWeight: on ? 700 : 500,
      color: on ? 'var(--ink)' : 'var(--ink-2)', padding: '8px 12px',
    }}>{children}</button>
  );
}

/* ------------------------------------------------------------------ card --- */

function ExpenseCard({ row, onChanged }: { row: VehicleExpense; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const label = EXPENSE_KINDS.find((k) => k.value === row.kind)?.label ?? row.kind;

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>{row.vehicleName}</strong>
            <span className="pill" style={{ fontSize: 11 }}>{label}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              {formatDate(row.spentOn)}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3 }}>
            {row.vendor ?? '—'}
            {row.litres ? ` · ${row.litres} L` : ''}
            {row.odometerKm ? ` · ${row.odometerKm.toLocaleString()} km` : ''}
            {row.responsibleName ? ` · ${row.responsibleName}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', flex: 'none' }}>
          <strong>{formatPaise(row.amountPaise)}</strong>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            {/* ⚠ Says which photos are MISSING, not just which exist. A clerk scanning the list
                needs to spot the gap — a fuel entry with no bill is the one to chase. */}
            {row.hasBill ? '📄' : ''}{row.hasMeter ? '📷' : ''}
            {!row.hasBill && !row.hasMeter ? 'no photo' : ''}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }}
          onClick={() => setOpen(!open)}>
          {open ? 'Hide photos' : 'Photos'}
        </button>
        <button className="btn" style={{ fontSize: 12, padding: '4px 10px', marginLeft: 'auto' }}
          onClick={async () => {
            if (!confirm('Delete this entry? The photos are deleted too.')) return;
            try { await expensesApi.remove(row.id); onChanged(); }
            catch { alert('Could not delete.'); }
          }}>
          Delete
        </button>
      </div>

      {open && (
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <PhotoSlot expenseId={row.id} which="bill"  has={row.hasBill}  label="Bill" onChanged={onChanged} />
          <PhotoSlot expenseId={row.id} which="meter" has={row.hasMeter} label="Meter / odometer" onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- photo --- */

/**
 * One photo slot — take it, replace it, view it.
 *
 * ⚠ `capture="environment"` opens the CAMERA on a phone rather than a file browser. The clerk is
 * standing at the pump; one tap and shoot. On a laptop the same input falls back to a file picker,
 * so a single control serves both with no second code path.
 *
 * ⚠ The image is fetched with the auth header and shown as a blob. The endpoint checks who is
 * asking, and an `<img src="/api/…">` cannot carry a bearer token.
 */
function PhotoSlot({ expenseId, which, has, label, onChanged }: {
  expenseId: string; which: 'bill' | 'meter'; has: boolean; label: string; onChanged: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!has) { setSrc(null); return; }
    let url: string | null = null;
    let cancelled = false;

    expensesApi.photoBlob(expenseId, which)
      .then((b) => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      })
      .catch(() => { /* leave the placeholder */ });

    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [expenseId, which, has]);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try { await expensesApi.attachPhoto(expenseId, which, f); onChanged(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Could not upload.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ width: 150 }}>
      <div className="label" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{
        width: 150, height: 110, borderRadius: 8, overflow: 'hidden',
        border: '1px dashed var(--line)', display: 'grid', placeItems: 'center',
        background: 'var(--surface-2, transparent)', marginBottom: 6,
      }}>
        {src
          ? <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{has ? 'Loading…' : 'No photo'}</span>}
      </div>

      <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />

      <button className="btn" style={{ fontSize: 12, padding: '4px 10px', width: '100%' }}
        disabled={busy} onClick={() => fileRef.current?.click()}>
        {busy ? 'Uploading…' : has ? 'Retake' : 'Take photo'}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- report --- */

function CostReport({ rows }: { rows: VehicleCost[] }) {
  if (rows.length === 0) {
    return <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-3)' }}>
      Nothing spent in this period.
    </div>;
  }

  const grand = rows.reduce((s, r) => s + r.totalPaise, 0);

  return (
    <>
      <div className="card" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: 'var(--ink-2)', fontSize: 13 }}>{rows.length} vehicles</span>
        <strong>{formatPaise(grand)}</strong>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
          <thead>
            <tr>
              <Th>Vehicle</Th><Th r>Fuel</Th><Th r>Service &amp; repair</Th>
              <Th r>Other</Th><Th r>Total</Th><Th r>km/L</Th><Th r>₹ / km</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.vehicleId} style={{ borderTop: '1px solid var(--line-2)' }}>
                <Td>
                  <strong>{r.vehicleName}</strong>
                  {r.registrationNumber && (
                    <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {r.registrationNumber}
                    </div>
                  )}
                </Td>
                <Td r>{formatPaise(r.fuelPaise)}</Td>
                <Td r>{formatPaise(r.maintenancePaise)}</Td>
                <Td r>{formatPaise(r.otherPaise)}</Td>
                <Td r><strong>{formatPaise(r.totalPaise)}</strong></Td>
                {/* ⚠ "—", never 0. A null means we don't have two usable odometer readings, and
                    printing 0 km/L next to a bus registration is a claim, not a gap. */}
                <Td r>{r.kmPerLitre ?? '—'}</Td>
                <Td r>{r.costPerKm ? `₹${r.costPerKm}` : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.6 }}>
        km/L and ₹/km need odometer readings on at least two fuel entries for that vehicle. A dash
        means there aren’t enough readings yet — not that the vehicle didn’t run.
      </p>
    </>
  );
}

function Th({ children, r }: { children: React.ReactNode; r?: boolean }) {
  return <th style={{ textAlign: r ? 'right' : 'left', padding: '10px 12px', fontSize: 12, color: 'var(--ink-2)' }}>{children}</th>;
}
function Td({ children, r }: { children: React.ReactNode; r?: boolean }) {
  return <td style={{ textAlign: r ? 'right' : 'left', padding: '10px 12px', fontSize: 13.5 }}>{children}</td>;
}

/* ------------------------------------------------------------------ form --- */

function AddExpenseDialog({ vehicles, onClose, onDone }: {
  vehicles: Vehicle[]; onClose: () => void; onDone: () => void;
}) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id ?? '');
  const [kind, setKind] = useState<ExpenseKind>('Fuel');
  const [spentOn, setSpentOn] = useState(today());
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [odometer, setOdometer] = useState('');
  const [litres, setLitres] = useState('');
  const [note, setNote] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFuel = kind === 'Fuel';

  const save = async () => {
    if (!vehicleId) { setError('Choose a vehicle.'); return; }
    if (!amount.trim()) { setError('Enter the amount.'); return; }
    setBusy(true); setError(null);
    try {
      const created = await expensesApi.record({
        vehicleId, kind, spentOn,
        amountPaise: toPaise(amount),
        vendor: vendor.trim() || null,
        billNumber: billNumber.trim() || null,
        odometerKm: odometer.trim() ? Number(odometer) : null,
        litres: isFuel && litres.trim() ? Number(litres) : null,
        note: note.trim() || null,
      });
      // ⚠ Saved FIRST, photos after. The entry must survive even if the clerk walks away before
      // photographing anything — an amount with no photo is useful; a photo with no amount is not.
      void created;
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 22, width: 440, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 14 }}>Add a bill</h2>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <label className="label">Vehicle</label>
        <select className="input" value={vehicleId} style={{ marginBottom: 12 }}
          onChange={(e) => setVehicleId(e.target.value)}>
          {vehicles.length === 0 && <option value="">No vehicles yet</option>}
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>

        <label className="label">What for</label>
        <select className="input" value={kind} style={{ marginBottom: 12 }}
          onChange={(e) => setKind(e.target.value as ExpenseKind)}>
          {EXPENSE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label className="label">Date</label>
            <input className="input" type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Amount ₹</label>
            {/* ⚠ inputMode numeric so a phone shows the number pad, not the full keyboard. */}
            <input className="input mono" inputMode="decimal" value={amount} placeholder="2500"
              onChange={(e) => setAmount(e.target.value)} />
          </div>
        </div>

        {isFuel && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="label">Litres</label>
              <input className="input mono" inputMode="decimal" value={litres} placeholder="42.5"
                onChange={(e) => setLitres(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label">Odometer km</label>
              <input className="input mono" inputMode="numeric" value={odometer} placeholder="84210"
                onChange={(e) => setOdometer(e.target.value)} />
            </div>
          </div>
        )}

        {!isFuel && (
          <>
            <label className="label">Odometer km <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
            <input className="input mono" inputMode="numeric" value={odometer} placeholder="84210"
              style={{ marginBottom: 12 }} onChange={(e) => setOdometer(e.target.value)} />
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label className="label">Bunk / garage</label>
            <input className="input" value={vendor} placeholder="HP, Bharamasagara"
              onChange={(e) => setVendor(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Bill no.</label>
            <input className="input mono" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} />
          </div>
        </div>

        <label className="label">Note <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
        <input className="input" value={note} style={{ marginBottom: 8 }}
          onChange={(e) => setNote(e.target.value)} />

        {/* ⚠ Told plainly, because a clerk who expects a camera here and doesn't get one will
            assume the photo feature is missing and stop looking for it. */}
        <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, marginBottom: 18 }}>
          Save first, then tap <strong>Photos</strong> on the entry to take a picture of the bill and
          the meter.
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
